import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import helpers from "helpers";
import queryClient from "queries";
const REACT_APP_CHARGE_STATION_URL = process.env.REACT_APP_CHARGE_STATION_URL || 'http://localhost';

export type OcppConnectorStatus =
    | "Available"
    | "Preparing"
    | "Charging"
    | "SuspendedEV"
    | "SuspendedEVSE"
    | "Finishing"
    | "Reserved"
    | "Unavailable"
    | "Faulted";

export type OcppChargePointErrorCode =
    | "NoError"
    | "ConnectorLockFailure"
    | "EVCommunicationError"
    | "GroundFailure"
    | "HighTemperature"
    | "InternalError"
    | "LocalListConflict"
    | "OtherError"
    | "OverCurrentFailure"
    | "OverVoltage"
    | "PowerMeterFailure"
    | "PowerSwitchFailure"
    | "ReaderFailure"
    | "ResetFailure"
    | "UnderVoltage"
    | "WeakSignal";


export interface ChargeStationConnector {
    id: number;
    chargeStationId: number;
    connectorId: number;

    status: OcppConnectorStatus;
    errorCode: OcppChargePointErrorCode;

    statusTimestamp: string; // ISO date string
    createdAt: string; // ISO date string
    updatedAt: string; // ISO date string
}


export interface ChargePoint {
    id?: number;
    cpid?: string;
    name?: string;

    chargePointVendor?: string | null;
    chargePointModel?: string | null;
    chargePointSerialNumber?: string | null;

    serialNumber?: string | null;
    iccid?: string | null;
    imsi?: string | null;

    lastHeartbeatAt?: string; // ISO date string
    createdAt?: string; // ISO date string
    updatedAt?: string; // ISO date string

    status: "APPROVED" | "UNAPPROVED" | "DENIED";
    address?: string | null;
    gunCount?: number;
    hotline?: string | null;
    maxPowerKw?: number | null;
    onlineStatus?: boolean;

    connectors?: ChargeStationConnector[];

}

export type ChargePointListResponse = ChargePoint[];



export type ChargeStationUpsertPayload = Omit<ChargePoint, "id" | "status" | "connectors"> & {
    id?: number | string;
};

export function useCreateChargeStation() {
    return useMutation({
        mutationKey: ['charge_station/create'],
        mutationFn: (payload: ChargeStationUpsertPayload) =>
            axios.post<ChargePoint>(`${REACT_APP_CHARGE_STATION_URL}/charge_station`, helpers.cleanEntity(payload)).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['charge_station/fetch_entities'] });
        },
    });
}

export function useUpdateChargeStation() {
    return useMutation({
        mutationKey: ['charge_station/update'],
        mutationFn: ({ id, ...payload }: ChargeStationUpsertPayload) =>
            axios.patch<ChargePoint>(`${REACT_APP_CHARGE_STATION_URL}/charge_station/${id}`, helpers.cleanEntity(payload)).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['charge_station/fetch_entities'] });
            queryClient.invalidateQueries({ queryKey: ['charge_station/fetch_entity'] });
        },
    });
}


export function useGetChargeStations() {
    return useQuery({
        queryKey: ['charge_station/fetch_entities'],
        queryFn: () => axios.get<ChargePointListResponse>(`${REACT_APP_CHARGE_STATION_URL}/charge_station`).then(response => response.data),
    });
}


export function useGetChargeStation() {
    return useMutation({
        mutationKey: ['charge_station/fetch_entity'],
        mutationFn: (id: any) => axios.get<ChargePoint>(`${REACT_APP_CHARGE_STATION_URL}/charge_station/${id}`).then(response => response.data),
    });
}

export function useApprovalChargeStation() {
    return useMutation({
        mutationKey: ['charge_station/approval'],
        mutationFn: ({ chargeStationId, decision }: { chargeStationId: number; decision: "approve" | "deny" }) =>
            axios
                .post<{ decision: "approve" | "deny" }>(
                    `${REACT_APP_CHARGE_STATION_URL}/charge_station/${chargeStationId}/approval`,
                    { decision }
                )
                .then(response => response.data),
    });
}



/** Phần gọi connector để hiển thị, có thể truy vấn nhiều lần ... cứ mỗi 10 giây truy vấn lại một lần ... */

export const ConnectorStatus = {
    Preparing: 'Preparing',
    Charging: 'Charging',
    Finishing: 'Finishing',
    Available: 'Available',
    Unavailable: 'Unavailable',
    Faulted: 'Faulted'
} as const

export interface ConnectorDTO {
    id: number;
    chargeStationId: number;
    connectorId: number;
    status: keyof typeof ConnectorStatus; // hiện data là "Charging"
    errorCode: string; // hiện data là "NoError"
    statusTimestamp: string; // ISO date string
    createdAt: string; // ISO date string
    updatedAt: string; // ISO date string
}

export const VehicleStatus = {
    UNAPPROVED: 'UNAPPROVED',
    WHITELIST: 'WHITELIST',
    BANNED: 'BANNED',
    APPROVED: 'APPROVED'
} as const

export type VehicleStatus = (typeof VehicleStatus)[keyof typeof VehicleStatus]




export interface VehicleDTO {
    id: number;
    tagId: string;
    vehicleType: string | null;
    licensePlate: string | null;
    brand: string | null;
    manufactureYear: number | null;
    status: VehicleStatus; // hiện data là "UNAPPROVED"
}

export interface SessionMetricsDTO {
    socStart: number;
    socEnd: number;
    powerKw: number;
    sessionKw: number;
    energyKwh: number;
    voltageV: number;
    currentA: number;
    durationSeconds: number;
}

export interface ChargeTransaction {
    id: number;
    ocppTransactionId: number;
    connectorId: number;
    meterStart: number;
    meterStop: number | null;
    startedAt: string; // ISO date string
    stoppedAt: string | null; // ISO date string | null
    status: 'STARTED' | 'STOPPED'; // hiện data là "STARTED"
    metrics: SessionMetricsDTO;

    vehicleId?: number;
    vehicle?: VehicleDTO;
    chargeStationId?: number;
    charge_station?: ChargePoint
}

export interface ConnectorWithLatestSessionDTO {
    connector: ConnectorDTO;
    latestSession: ChargeTransaction;
}

export function useGetConnector(charge_station_id) {
    return useQuery({
        queryKey: ['connector/fetch_entity'],
        queryFn: () => axios.get<ConnectorWithLatestSessionDTO[]>(`${REACT_APP_CHARGE_STATION_URL}/connector/${charge_station_id}`).then(response => response.data),
    });
}





/********************************* Vehicle ************* */
/********************************* Vehicle ************* */
/********************************* Vehicle ************* */
/********************************* Vehicle ************* */
/********************************* Vehicle ************* */
/********************************* Vehicle ************* */



export interface Vehicle {
    id: number;
    tagId: string;

    createdAt: string; // ISO datetime
    updatedAt: string; // ISO datetime

    status: VehicleStatus;

    brand: string | null;
    licensePlate: string | null;
    manufactureYear: number | null;
    vehicleType: string | null;
}

export type VehicleListResponse = Vehicle[];


export function useGetVehicles() {
    return useQuery({
        queryKey: ['charge_station_vehicle/fetch_entities'],
        queryFn: () => axios.get<VehicleListResponse>(`${REACT_APP_CHARGE_STATION_URL}/vehicle`).then(response => response.data),
    });
}


/** lấy thông tin một xe ra  */

export function useGetVehicle() {
    return useMutation({
        mutationKey: ['charge_station_vehicle/fetch_entity'],
        mutationFn: (vehicle_id) => axios.get<{
            vehicle_data: Vehicle,
            "analytics": {
                "totalEnergyKwh": number,
                "totalDurationMinutes": number,
                "totalTransactions": number
            }
        }>(`${REACT_APP_CHARGE_STATION_URL}/vehicle/${vehicle_id}`).then(response => response.data),
    });
}


export function useCreateVehicle() {
    return useMutation({
        mutationKey: ['charge_station_vehicle/create'],
        mutationFn: (payload: Vehicle) =>
            axios.post<ChargePoint>(`${REACT_APP_CHARGE_STATION_URL}/vehicle`, helpers.cleanEntity(payload)).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['charge_station_vehicle/fetch_entities'] });
        },
    });
}

export function useUpdateVehicle() {
    return useMutation({
        mutationKey: ['charge_station_vehicle/update'],
        mutationFn: ({ id, ...payload }: Vehicle) =>
            axios.patch<ChargePoint>(`${REACT_APP_CHARGE_STATION_URL}/vehicle/${id}`, helpers.cleanEntity(payload)).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['charge_station_vehicle/fetch_entities'] });
            queryClient.invalidateQueries({ queryKey: ['charge_station_vehicle/fetch_entity'] });
        },
    });
}



/********************************* charge_transaction ************* */
/********************************* charge_transaction ************* */
/********************************* charge_transaction ************* */
/********************************* charge_transaction ************* */
/********************************* charge_transaction ************* */


export interface TimeSeriesPoint {
    timestamp: string;
    value: number;
}

export interface ChargeTransactionSeries {
    powerKw: TimeSeriesPoint[];
    currentA: TimeSeriesPoint[];
    voltageV: TimeSeriesPoint[];
}

export interface ChargeTransactionDetailResponse {
    transaction: ChargeTransaction;
    series: ChargeTransactionSeries;
}

export interface ChargeTransactionAnalysis {
    totalEnergyKwh: number;
    totalDurationMinutes: number;
}

export interface ChargeTransactionListResponse {
    data: ChargeTransaction[];
    analysis: ChargeTransactionAnalysis;
}

export interface ChargeTransactionTodayAnalysis extends ChargeTransactionAnalysis {
    totalTransactions: number;
}

export function useGetChargeTransactions(object: any, enabled = true) {
    const EndURL = helpers.buildEndUrl(object);
    return useQuery({
        queryKey: ['charge_station_transaction/fetch_entities', object],
        queryFn: () => axios.get<ChargeTransactionListResponse>(`${REACT_APP_CHARGE_STATION_URL}/charge_transaction${EndURL}`).then(response => {
            let { data, headers } = response;
            return {
                entities: data.data,
                analysis: data.analysis,
                totalItems: Number(headers['x-total-count'] || data.data.length || 0)
            }
        }),
        enabled: enabled && !!object,
    });
}

export function useGetChargeTransactionTodayAnalysis() {
    return useQuery({
        queryKey: ['charge_station_transaction/analytics_today'],
        queryFn: () => axios
            .get<ChargeTransactionTodayAnalysis>(`${REACT_APP_CHARGE_STATION_URL}/charge_transaction/analytics_today`)
            .then(response => response.data),
    });
}

export function useGetChargeTransactionsViaVehicleID(vehicle_id: any) {
    return useQuery({
        queryKey: ['charge_station_transaction/fetch_entities_via_vehicle_id'],
        queryFn: () => axios.get<{
            vehicle: VehicleDTO,
            sessions: ChargeTransaction[],
            charge_station: ChargePoint
        }>(`${REACT_APP_CHARGE_STATION_URL}/charge_transaction/vehicle_id/${vehicle_id}`).then(response => response.data),
        enabled: !!vehicle_id
    });
}


export function useGetTransactionDetail() {
    return useMutation({
        mutationKey: ['charge_station_transaction/fetch_entity'],
        mutationFn: (charge_transaction_id) => axios.get<ChargeTransactionDetailResponse>(`${REACT_APP_CHARGE_STATION_URL}/charge_transaction/${charge_transaction_id}`).then(response => response.data),
    });
}



/********************************* ACTION ************* */

export function useStartTransaction() {
    return useMutation({
        mutationKey: ['charge_station_transaction/start'],
        mutationFn: ({
            cpid,
            connectorId,
            idTag
        }: {
            cpid: string,
            connectorId: number,
            idTag: any
        }) => axios.post<any>(`${REACT_APP_CHARGE_STATION_URL}/ocpp/remote-start/`, {
            cpid,
            connectorId,
            idTag
        }).then(response => response.data),
    });
}

export function useStopTransaction() {
    return useMutation({
        mutationKey: ['charge_station_transaction/stop'],
        mutationFn: ({
            cpid,
            connectorId
        }: {
            cpid: string,
            connectorId: number
        }) => axios.post<any>(`${REACT_APP_CHARGE_STATION_URL}/ocpp/remote-stop/`, {
            cpid,
            connectorId
        }).then(response => response.data),
    });
}




/** Tải về excel bản đang xem */
export function useDownloadChargeTransaction() {
    return useMutation({
        mutationKey: ["charge_station_transaction/download"],
        mutationFn: (object) => axios.get<Blob>(`${REACT_APP_CHARGE_STATION_URL}/charge_transaction/download${helpers.buildEndUrl(object)}`, { responseType: 'blob', }).then(response => response.data),
    })
}

/********************************* Helpers ************* */



export function helper_fmtNumber(n: number | null, digits = 1) {
    if (n == null || !Number.isFinite(n)) return '—';
    return n.toFixed(digits);
}

export function helper_fmtTime(sec: number | null) {
    if (sec == null || !Number.isFinite(sec)) return '—';
    const s = Math.max(0, Math.floor(sec));
    const hh = Math.floor(s / 3600);
    const mm = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    const pad = (x: number) => String(x).padStart(2, '0');
    return hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${mm}:${pad(ss)}`;
}


